/**
 * /c/:slug/discovery - the Discovery sender console (CHAP-593).
 *
 * Counts per state, the gate checklist with set and clear, the CSV import
 * (counts only shown back), a sandboxed preview of each email and variant,
 * seed, prepare and release (release needs the frozen count typed), stop,
 * mark replied and retry notices.
 *
 * Every call goes through useDiscovery.ts and the app's fetch helper, so each
 * POST carries the CSRF header. The server is the authority on every rule:
 * session, admin role, DISCOVERY_SENDERS, no impersonation, and each release
 * refusal. The page's own checks (the typed count, the CSV header row, the
 * postal address) only stop an obvious mistake before it is sent.
 */
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError } from '../lib/api.js';
import {
  fetchDiscoveryPreview,
  postImport,
  useDiscoveryAction,
  useDiscoveryStatus,
  useRefreshDiscovery,
  useSeedMarkLock,
  type PreviewResult,
} from '../hooks/useDiscovery.js';
import {
  CSV_COLUMNS,
  EMAIL_NOS,
  EMAIL_RX,
  GATES,
  MAX_BATCH,
  MAX_CSV_CHARS,
  UUID_RX,
  VARIANTS,
  acceptedCount,
  approvalMatch,
  backfillText,
  cancelBatch,
  clearGate,
  csvMissingColumns,
  describeError,
  describeImportError,
  describeRecipientError,
  gateIsSet,
  gateReady,
  gateSetBody,
  haltReasonText,
  isCancellable,
  lockedWrite,
  markSeedChecked,
  newestMatchingSeed,
  numericLeaves,
  postalProblem,
  preparedStillReleasable,
  readCancelResult,
  readImportResult,
  readPrepareResult,
  readSeedResult,
  refusalText,
  releaseCandidate,
  releaseGate,
  retryNoticesOutcome,
  seedCheckProblem,
  seedCheckedBody,
  seedDraftProblem,
  seedEntries,
  seedMarkProblem,
  shaPrefix,
  unsentCount,
  type ConfigRow,
  type CountRow,
  type Dispatch,
  type DiscoveryStatus,
  type EmailNo,
  type GateContext,
  type GateDef,
  type GateState,
  type GateWrite,
  type ImportOutcome,
  type MarkLock,
  type SeedEntry,
  type Variant,
} from './discovery/model.js';
import { PreviewFrame } from './discovery/PreviewFrame.js';
import { ReleasePanel } from './discovery/ReleasePanel.js';
import { SeedInboxNote } from './discovery/SeedInboxNote.js';

/** The page's one Mark checked lock, and whether a press is waiting for its refetch. */
interface SeedMarks {
  lock: MarkLock;
  held: boolean;
}

export default function Discovery() {
  const { slug = '' } = useParams();
  const status = useDiscoveryStatus(slug);
  const marks = useSeedMarkLock();

  if (status.isLoading) return <div className="p-8 text-center text-sm text-neutral">Loading Discovery...</div>;
  if (status.error) return <AccessMessage error={status.error} />;
  if (!status.data) return null;
  const s = status.data;

  return (
    <div className="space-y-4 min-w-0">
      <Header slug={slug} halt={gateIsSet(s.gates.halt) ? s.gates.halt.value : null} />
      {(s.config.length > 0 || s.configGaps !== null) && <ConfigCard rows={s.config} gaps={s.configGaps} />}
      <Card title="Counts" sub="Where every brother on the list stands. Numbers only.">
        <CountGrid rows={s.counts} empty="No counts yet. Import the list to begin." />
      </Card>
      <GatesCard slug={slug} status={s} marks={marks} />
      <ImportCard slug={slug} />
      <PreviewCard slug={slug} status={s} />
      <SeedCard slug={slug} />
      <PrepareReleaseCard slug={slug} status={s} />
      <DispatchesCard slug={slug} status={s} marks={marks} />
      <RecipientCard slug={slug} retryable={s.retryable} />
    </div>
  );
}

// ---------------------------------------------------------------------------

function AccessMessage({ error }: { error: Error }) {
  const code = error instanceof ApiError ? error.status : 0;
  const body = error instanceof ApiError && error.body && typeof error.body === 'object' ? (error.body as Record<string, unknown>) : null;
  const text =
    code === 403 && body?.refusal === 'impersonating'
      ? 'Discovery is closed while you are viewing as someone else. Stop impersonating, then open this page again.'
      : code === 401
        ? 'Your session has ended. Sign in again to use Discovery.'
        : code === 403
          ? "Discovery is open to the campaign's senders only."
          : code === 404
            ? 'Discovery is not switched on for this chapter.'
            : `Discovery could not load. ${describeError(error)}`;
  return <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-sm text-neutral">{text}</div>;
}

function Header({ slug, halt }: { slug: string; halt: string | null }) {
  const stop = useDiscoveryAction<void>(slug, 'stop');
  const onStop = () => {
    const question =
      'Stop sending now? This sets halt and cancels any frozen batch. A released batch pauses, and goes on ' +
      'the moment halt is cleared; to end a released batch, cancel it under Dispatches.';
    if (!window.confirm(question)) return;
    stop.mutate();
  };
  return (
    <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 flex items-start justify-between flex-wrap gap-3 min-w-0">
      <div className="min-w-0">
        <h2 className="text-navy text-xl sm:text-2xl font-bold">Discovery</h2>
        <p className="text-neutral text-sm mt-1">
          Five emails to the Foundation's list. Nothing sends until every gate holds and you type the frozen count.
        </p>
        {halt !== null && (
          <p role="status" className="mt-2 text-sm font-semibold text-red-700 break-words">
            Halt is set. Nothing sends until it is cleared. {haltReasonText(halt) ?? `Reason: ${halt}`}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <button
          type="button"
          onClick={onStop}
          disabled={stop.isPending}
          className="px-4 py-2 rounded bg-red-700 text-white text-sm font-semibold hover:bg-red-800 disabled:opacity-50"
        >
          {stop.isPending ? 'Stopping...' : 'Stop sending'}
        </button>
        {stop.isError && <span role="alert" className="text-xs text-red-700">{describeError(stop.error)}</span>}
        {stop.isSuccess && <span role="status" className="text-xs text-green-700">Stopped.</span>}
      </div>
    </header>
  );
}

// The server's own readout of what is configured. Release refuses while
// configuration is unset, so this is the first place to look when it does.
function ConfigCard({ rows, gaps }: { rows: ConfigRow[]; gaps: string[] | null }) {
  return (
    <Card title="Configuration" sub="What the server has set. Release refuses while required settings are missing.">
      {gaps === null ? null : gaps.length > 0 ? (
        <div role="status" className="mb-3 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 min-w-0">
          <p className="font-semibold">
            {gaps.length === 1 ? 'One gap' : `${gaps.length} gaps`} in the configuration:
          </p>
          <ul className="mt-1 list-disc pl-5 space-y-0.5">
            {gaps.map((g) => (
              <li key={g} className="break-words">
                {refusalText(g)}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="mb-3 text-xs text-green-700">No gaps in the configuration.</p>
      )}
      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 min-w-0">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center gap-2 min-w-0">
            <span
              aria-hidden="true"
              className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${r.ok === true ? 'bg-positive' : r.ok === false ? 'bg-negative' : 'bg-gray-300'}`}
            />
            <dt className="text-xs text-neutral">{r.label}</dt>
            <dd className="text-xs font-medium text-navy truncate">{r.value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Gates

/**
 * What a gate's readiness depends on: this build's copy sha for its email
 * (compared, never shown), the dispatches, and the postal address a checked
 * seed must have gone out with.
 */
function gateContext(def: GateDef, status: DiscoveryStatus): GateContext {
  return {
    buildSha: def.emailNo ? status.copyShas[def.emailNo] ?? null : null,
    dispatches: status.dispatches,
    postal: status.gates.postal_address.value,
    versionsNeeded: status.versionsNeeded,
  };
}

function GatesCard({ slug, status, marks }: { slug: string; status: DiscoveryStatus; marks: SeedMarks }) {
  const save = useDiscoveryAction<GateWrite>(slug, 'gates');
  const ready = GATES.filter((g) => gateReady(g, status.gates[g.key], gateContext(g, status))).length;
  const markWrite = lockedWrite((body) => save.mutateAsync(body), marks.lock);
  return (
    <Card
      title="Gates"
      sub={`${ready} of ${GATES.length} ready. Release refuses unless every gate holds; halt is the one that must be clear.`}
    >
      <ul className="divide-y divide-gray-100 min-w-0">
        {GATES.map((g) => (
          <GateRow
            key={g.key}
            def={g}
            state={status.gates[g.key]}
            ctx={gateContext(g, status)}
            pending={save.isPending && save.variables?.key === g.key}
            write={(body) => save.mutate(body)}
            marks={marks}
            markWrite={markWrite}
          />
        ))}
      </ul>
      {status.otherGates.length > 0 && (
        <div className="mt-3 text-xs text-neutral min-w-0">
          <p className="font-medium text-navy">Other gates the server reports</p>
          <ul className="mt-1 space-y-0.5">
            {status.otherGates.map((g) => (
              <li key={g.key} className="break-all">
                <span className="font-mono">{g.key}</span>: {g.value ?? 'not set'}
              </li>
            ))}
          </ul>
        </div>
      )}
      {save.isError && <ErrorBox>{describeError(save.error)}</ErrorBox>}
    </Card>
  );
}

function GateRow({
  def,
  state,
  ctx,
  pending,
  write,
  marks,
  markWrite,
}: {
  def: GateDef;
  state: GateState;
  ctx: GateContext;
  pending: boolean;
  write: (body: GateWrite) => void;
  marks: SeedMarks;
  /** Mark checked's write, which releases the page's lock once the status is read again. */
  markWrite: (body: GateWrite) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const isSet = gateIsSet(state);
  const good = gateReady(def, state, ctx);
  const inputId = `dz-gate-${def.key}`;
  const mono = def.kind === 'sha' || def.kind === 'seed' ? 'font-mono' : '';
  const problem = !isSet
    ? null
    : def.key === 'postal_address'
      ? postalProblem(state.value)
      : def.kind === 'seed'
        ? seedCheckProblem(state.value ?? '', def.emailNo ?? null, ctx)
        : null;
  // The approved sha is compared with this build's, and only the verdict is shown.
  const storedMatch = def.kind === 'sha' && isSet ? approvalMatch(state.value, ctx.buildSha) : null;
  const typedMatch = def.kind === 'sha' ? approvalMatch(draft, ctx.buildSha) : null;
  const dispatches = ctx.dispatches ?? [];
  const entries = def.kind === 'seed' && isSet ? seedEntries(state.value, dispatches) : [];
  // A hint per version a batch needs, plus any version already listed.
  const hintVersions = (['ask', 'no_ask'] as const).filter(
    (v) => (ctx.versionsNeeded ?? ['ask']).includes(v) || entries.some((e) => e.dispatch?.variant === v),
  );
  const hints =
    def.kind === 'seed' && def.emailNo
      ? hintVersions.map((version) => ({
          version,
          seed: newestMatchingSeed(dispatches, def.emailNo!, ctx.buildSha, version),
        }))
      : [];
  // A seed list the server would refuse unread (not one to four uuids, or an id twice) is not sent.
  const seedProblem = def.kind === 'seed' ? seedDraftProblem(draft) : null;
  const draftOk = def.kind === 'seed' ? seedProblem === null : draft.trim() !== '';
  const haltWhy = def.key === 'halt' && isSet ? haltReasonText(state.value) : null;

  // An approval is typed fresh each time, never pre-filled: if it matches
  // this build, pre-filling it would put the build's sha on the page.
  function open() {
    setDraft(isSet && def.kind !== 'sha' ? state.value ?? '' : '');
    setEditing(true);
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!draftOk) return;
    write(gateSetBody(def.key, draft));
    setEditing(false);
  }
  function clear() {
    clearGate(def, write, (question) => window.confirm(question));
  }
  function markSeed(d: Dispatch) {
    if (markSeedChecked(d, state.value, dispatches, markWrite, (question) => window.confirm(question), marks.lock)) {
      setEditing(false);
    }
  }

  return (
    <li className="py-3 min-w-0">
      <div className="flex items-start justify-between gap-3 flex-wrap min-w-0">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span
              aria-hidden="true"
              className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${good ? 'bg-positive' : def.inverted ? 'bg-negative' : 'bg-gray-300'}`}
            />
            <span className="text-sm font-medium text-navy">{def.label}</span>
            <span className="sr-only">{good ? '(ready)' : '(not ready)'}</span>
          </div>
          {isSet && def.kind === 'seed' ? (
            <SeedList entries={entries} />
          ) : isSet ? (
            <div className={`mt-1 text-xs break-all ${mono}`}>
              {def.kind === 'sha' ? `${shaPrefix(state.value)}...` : state.value}
            </div>
          ) : (
            <div className="mt-1 text-xs text-neutral">Not set</div>
          )}
          {storedMatch && <MatchNote result={storedMatch} />}
          {haltWhy && <div className="mt-1 text-xs font-medium text-red-700">{haltWhy}</div>}
          {(state.setBy || state.setAt) && (
            <div className="text-[11px] text-neutral">
              Set{state.setBy ? ` by ${state.setBy}` : ''}
              {state.setAt ? `, ${fmtTime(state.setAt)}` : ''}
            </div>
          )}
          {problem && <div className="mt-1 text-xs text-amber-800">{problem}</div>}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={editing ? () => setEditing(false) : open}
            disabled={pending}
            aria-expanded={editing}
            aria-controls={`${inputId}-form`}
            className="text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            {editing ? 'Cancel' : isSet ? 'Change' : 'Set'}
          </button>
          {isSet && !editing && (
            <button
              type="button"
              onClick={clear}
              disabled={pending}
              className="text-xs px-3 py-1.5 rounded border border-gray-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      {editing && (
        <form id={`${inputId}-form`} onSubmit={submit} className="mt-2 space-y-2 min-w-0">
          <label htmlFor={inputId} className="block text-xs text-neutral">
            {def.hint}
          </label>
          {def.kind === 'seed' && (
            <SeedHint hints={hints} emailNo={def.emailNo} ctx={ctx} pending={pending || marks.held} onMark={markSeed} />
          )}
          {def.kind === 'text' ? (
            <textarea
              id={inputId}
              rows={2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          ) : (
            <input
              id={inputId}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              className={`w-full border border-gray-300 rounded px-3 py-2 text-sm ${mono}`}
            />
          )}
          {typedMatch && <MatchNote result={typedMatch} />}
          {seedProblem && draft.trim() !== '' && <p className="text-xs text-amber-800">{seedProblem}</p>}
          <button
            type="submit"
            disabled={!draftOk || pending}
            className="px-3 py-1.5 rounded bg-navy text-white text-sm font-medium disabled:opacity-50"
          >
            Save
          </button>
        </form>
      )}
    </li>
  );
}

/** "Matches this build" or "Does not match this build", and never the build's sha itself. */
function MatchNote({ result }: { result: 'match' | 'mismatch' }) {
  return result === 'match' ? (
    <div className="mt-1 text-xs font-medium text-green-700">Matches this build.</div>
  ) : (
    <div className="mt-1 text-xs font-medium text-red-700">Does not match this build.</div>
  );
}

/** A seed gate's value, one line per listed seed, with its version when the page knows it. */
function SeedList({ entries }: { entries: SeedEntry[] }) {
  return (
    <ul className="mt-1 space-y-0.5 text-xs min-w-0">
      {entries.map((e) => (
        <li key={e.id} className="break-all">
          <span className="font-medium text-navy">{e.dispatch?.variant ?? 'version unknown'}:</span>{' '}
          <span className="font-mono">{e.id}</span>
          {e.dispatch ? (
            <span className="text-neutral">
              {' '}
              (sha {shaPrefix(e.dispatch.copySha)}, {acceptedCount(e.dispatch)} delivered)
            </span>
          ) : (
            <span className="text-neutral"> (not among the recent dispatches)</span>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * For each version a batch needs, the newest seed of this build and a way to
 * mark it checked: offered only when release would take that seed, and in its
 * place, the reason it would not.
 */
export function SeedHint({
  hints,
  emailNo,
  ctx,
  pending,
  onMark,
}: {
  hints: Array<{ version: Variant; seed: Dispatch | null }>;
  emailNo?: EmailNo;
  ctx: GateContext;
  pending: boolean;
  onMark: (d: Dispatch) => void;
}) {
  return (
    <div className="text-[11px] text-neutral space-y-2 min-w-0">
      {hints.map(({ version, seed }) => {
        if (!seed) {
          return (
            <p key={version}>
              No {version} seed of email {emailNo} in this build's copy yet. Send one under Seed, read it, then mark it checked.
            </p>
          );
        }
        const problem = seedMarkProblem(seed, ctx);
        return (
          <div key={version} className="space-y-1 min-w-0">
            <p className="break-words">
              Newest {version} seed of this build: <span className="font-mono break-all">{seed.id}</span>, sha{' '}
              {shaPrefix(seed.copySha)}, {acceptedCount(seed)} delivered, sent {fmtTime(seed.createdAt) || 'at an unknown time'}.
            </p>
            {problem ? (
              <p className="text-amber-800 break-words">{problem}</p>
            ) : (
              <button
                type="button"
                onClick={() => onMark(seed)}
                disabled={pending}
                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Mark this {version} seed checked
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Import

function ImportCard({ slug }: { slug: string }) {
  const refresh = useRefreshDiscovery(slug);
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportOutcome | null>(null);

  async function onFile(file: File | undefined) {
    setError(null);
    setResult(null);
    if (!file) return;
    try {
      // A UTF-8 character is at most 4 bytes, so a file this big cannot be under the limit.
      if (file.size > MAX_CSV_CHARS * 4) {
        setError(`That file is too big. The import takes up to ${MAX_CSV_CHARS.toLocaleString('en-US')} characters.`);
        return;
      }
      const text = await file.text();
      if (text.length > MAX_CSV_CHARS) {
        setError(
          `That file is ${text.length.toLocaleString('en-US')} characters. The import takes up to ` +
            `${MAX_CSV_CHARS.toLocaleString('en-US')}. Nothing was sent.`,
        );
        return;
      }
      const missing = csvMissingColumns(text);
      if (missing.length) {
        setError(`The header row is missing: ${missing.join(', ')}. Nothing was sent.`);
        return;
      }
      setBusy(true);
      // Only the numbers in the response are kept; the list itself is not.
      setResult(readImportResult(await postImport(slug, text)));
      void refresh();
    } catch (err) {
      setError(describeImportError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <Card
      title="Import the list"
      sub={`A CSV with the columns ${CSV_COLUMNS.join(', ')}. group is ask or exclude. The server dedupes it and leaves out dnc, exclude, the priority brothers the Foundation sees in person (a no_ask row), no email, suppressed and anyone already a lead. Only counts come back.`}
    >
      <label htmlFor="dz-csv" className="block text-sm font-medium text-navy">
        CSV file
      </label>
      <input
        id="dz-csv"
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        disabled={busy}
        onChange={(e) => void onFile(e.target.files?.[0])}
        className="block mt-1 text-sm max-w-full"
      />
      {busy && <p className="mt-2 text-sm text-neutral">Importing...</p>}
      {error && <ErrorBox>{error}</ErrorBox>}
      {result && (
        <>
          <SeedInboxNote count={result.seedInboxesOnList} />
          <div className="mt-3">
            <CountGrid rows={result.counts} empty="Imported. The server sent no counts back." />
          </div>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Preview

function PreviewCard({ slug, status }: { slug: string; status: DiscoveryStatus }) {
  const [emailNo, setEmailNo] = useState<EmailNo>(1);
  const [variant, setVariant] = useState<Variant>('ask');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<(PreviewResult & { emailNo: EmailNo; variant: Variant }) | null>(null);

  async function load() {
    setBusy(true);
    setError(null);
    try {
      const result = await fetchDiscoveryPreview(slug, emailNo, variant);
      setPreview({ ...result, emailNo, variant });
    } catch (err) {
      setPreview(null);
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Preview" sub="The rendered email for a sample brother, in a sandboxed frame that runs no script.">
      <div className="flex items-end gap-3 flex-wrap min-w-0">
        <EmailSelect id="dz-preview-email" value={emailNo} onChange={setEmailNo} />
        <VariantSelect id="dz-preview-variant" value={variant} onChange={setVariant} />
        <button
          type="button"
          onClick={() => void load()}
          disabled={busy}
          className="px-4 py-2 rounded bg-navy text-white text-sm font-medium disabled:opacity-50"
        >
          {busy ? 'Loading...' : 'Show preview'}
        </button>
      </div>
      {error && <ErrorBox>{error}</ErrorBox>}
      {preview && (
        <div className="mt-3 space-y-2 min-w-0">
          {preview.subject && (
            <p className="text-sm break-words">
              <span className="text-neutral">Subject:</span> <span className="font-medium text-navy">{preview.subject}</span>
            </p>
          )}
          <ApprovalLine emailNo={preview.emailNo} copySha={preview.copySha} status={status} />
          <PreviewFrame html={preview.html} title={`Preview of email ${preview.emailNo}, ${preview.variant}`} />
          {preview.text && (
            <details className="text-sm min-w-0">
              <summary className="cursor-pointer text-navy font-medium">Plain-text version</summary>
              <pre className="mt-2 whitespace-pre-wrap break-words text-xs bg-gray-50 border border-gray-100 rounded p-3">
                {preview.text}
              </pre>
            </details>
          )}
          <p className="text-[11px] text-neutral">
            Images may not show here: the app does not load pictures from omega-zeta.com. The email itself does.
          </p>
        </div>
      )}
    </Card>
  );
}

/**
 * Whether the previewed copy is the approved copy. The build's sha stays off
 * the page: an approval has to come from Monk's package, not from here.
 */
function ApprovalLine({ emailNo, copySha, status }: { emailNo: EmailNo; copySha: string | null; status: DiscoveryStatus }) {
  const approved = status.gates[`approved_sha_e${emailNo}` as const];
  if (!gateIsSet(approved)) {
    return <p className="text-xs text-neutral">No copy of email {emailNo} has been approved yet.</p>;
  }
  const verdict = approvalMatch(approved.value, copySha);
  if (!verdict) return null;
  return verdict === 'match' ? (
    <p className="text-xs font-medium text-green-700">This copy is the approved copy of email {emailNo}.</p>
  ) : (
    <p className="text-xs font-medium text-red-700">This copy is not the approved copy of email {emailNo}.</p>
  );
}

// ---------------------------------------------------------------------------
// Seed, prepare, release

function SeedCard({ slug }: { slug: string }) {
  const seed = useDiscoveryAction<{ emailNo: EmailNo; variant: Variant }>(slug, 'seed');
  const [emailNo, setEmailNo] = useState<EmailNo>(1);
  const [variant, setVariant] = useState<Variant>('ask');
  const go = () => {
    if (!window.confirm(`Send the seed copies of email ${emailNo} (${variant}) to the seed inboxes?`)) return;
    seed.mutate({ emailNo, variant });
  };
  const sent = readSeedResult(seed.data);
  return (
    <Card
      title="Seed"
      sub="Sends the email exactly as it would go, to the seed inboxes only. Read it there, then mark its batch checked under Dispatches."
    >
      <div className="flex items-end gap-3 flex-wrap min-w-0">
        <EmailSelect id="dz-seed-email" value={emailNo} onChange={setEmailNo} />
        <VariantSelect id="dz-seed-variant" value={variant} onChange={setVariant} />
        <button
          type="button"
          onClick={go}
          disabled={seed.isPending}
          className="px-4 py-2 rounded bg-navy text-white text-sm font-medium disabled:opacity-50"
        >
          {seed.isPending ? 'Sending...' : 'Send seed copies'}
        </button>
      </div>
      {seed.isError && <ErrorBox>{describeError(seed.error)}</ErrorBox>}
      {seed.isSuccess && (
        <Ok>
          Seed sent for email {seed.variables?.emailNo} ({seed.variables?.variant}):{' '}
          {sent ? (
            <>
              batch <span className="font-mono break-all">{sent.dispatchId}</span>, sha {shaPrefix(sent.copySha)}
              {sent.count !== null ? `, to ${sent.count} seed ${sent.count === 1 ? 'inbox' : 'inboxes'}` : ''}.
            </>
          ) : (
            'the server did not say which batch.'
          )}{' '}
          Read it there, then mark that batch checked under Dispatches.
        </Ok>
      )}
    </Card>
  );
}

function PrepareReleaseCard({ slug, status }: { slug: string; status: DiscoveryStatus }) {
  const prepare = useDiscoveryAction<{ emailNo: EmailNo }>(slug, 'prepare');
  const release = useDiscoveryAction<{ dispatchId: string; typedCount: number }>(slug, 'release');
  const [emailNo, setEmailNo] = useState<EmailNo>(1);
  const [typed, setTyped] = useState('');

  const preparedRaw = readPrepareResult(prepare.data);
  // Offered only while status still lists it as releasable: a batch this tab
  // prepared and then cancelled (or another tab cancelled, released or
  // stopped) is not offered again.
  const prepared = preparedStillReleasable(preparedRaw, status.dispatches);
  const target = prepared
    ? {
        id: prepared.dispatchId,
        emailNo: prepare.variables?.emailNo ?? null,
        frozenCount: prepared.frozenCount,
        copySha: prepared.copySha,
        createdAt: null,
      }
    : releaseCandidate(status.dispatches);

  const doPrepare = () => {
    if (
      !window.confirm(
        `Freeze the next batch of email ${emailNo}? It takes up to ${MAX_BATCH} brothers not yet sent this email, and snapshots who they are and the copy sha.`,
      )
    ) {
      return;
    }
    release.reset();
    setTyped('');
    prepare.mutate({ emailNo });
  };
  const doRelease = () => {
    if (!target || !releaseGate(target.frozenCount, typed).ok) return;
    release.mutate(
      { dispatchId: target.id, typedCount: Number(typed.trim()) },
      {
        onSuccess: () => {
          setTyped('');
          prepare.reset();
        },
      },
    );
  };

  const released = release.isSuccess ? numericLeaves(release.data) : [];
  return (
    <Card
      title="Prepare and release"
      sub={`An email goes to the list in batches of up to ${MAX_BATCH}. Prepare freezes the next batch: who it goes to, of those not yet sent this email, and the copy sha. Release sends it, once every gate holds and you type the frozen count. When it has finished, prepare the next, until none remain.`}
    >
      <div className="flex items-end gap-3 flex-wrap min-w-0">
        <EmailSelect id="dz-prepare-email" value={emailNo} onChange={setEmailNo} />
        <button
          type="button"
          onClick={doPrepare}
          disabled={prepare.isPending}
          className="px-4 py-2 rounded bg-navy text-white text-sm font-medium disabled:opacity-50"
        >
          {prepare.isPending ? 'Freezing...' : 'Prepare'}
        </button>
      </div>
      {prepare.isError && <ErrorBox>{describeError(prepare.error)}</ErrorBox>}
      {prepare.isSuccess && !preparedRaw && (
        <ErrorBox>The server answered without a dispatch id and a whole-number count, so there is nothing to release.</ErrorBox>
      )}
      {prepared && (
        <Ok>
          Frozen: {prepared.frozenCount} for email {prepare.variables?.emailNo}, sha {shaPrefix(prepared.copySha)}.
          {backfillText(prepared.suppressionsBackfilled) && <> {backfillText(prepared.suppressionsBackfilled)}</>}
        </Ok>
      )}
      <div className="mt-4 pt-4 border-t border-gray-100 min-w-0">
        <h4 className="text-sm font-semibold text-navy mb-2">Release</h4>
        <ReleasePanel
          dispatch={target}
          typed={typed}
          onTypedChange={setTyped}
          onRelease={doRelease}
          pending={release.isPending}
          error={release.isError ? describeError(release.error) : null}
        />
        {release.isSuccess && (
          <>
            <Ok>Released. The send has started; the counts above follow it.</Ok>
            {released.length > 0 && (
              <div className="mt-3">
                <CountGrid rows={released} empty="" />
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Dispatches and one brother

function DispatchesCard({ slug, status, marks }: { slug: string; status: DiscoveryStatus; marks: SeedMarks }) {
  const mark = useDiscoveryAction<GateWrite>(slug, 'gates');
  const cancel = useDiscoveryAction<{ dispatchId: string }>(slug, 'cancel');
  const dispatches = status.dispatches;
  const ask = (question: string) => window.confirm(question);
  const markWrite = lockedWrite((body) => mark.mutateAsync(body), marks.lock);
  // The email's current checked-seed list, which Mark checked edits for one version only.
  const checkedList = (d: Dispatch) =>
    d.emailNo === 1 || d.emailNo === 2 || d.emailNo === 3 || d.emailNo === 4 || d.emailNo === 5
      ? status.gates[`seed_checked_e${d.emailNo}`].value
      : null;
  // What release compares a checked seed with: this build's copy of its email, and the current postal address.
  const seedCtx = (d: Dispatch): GateContext => ({
    buildSha:
      d.emailNo === 1 || d.emailNo === 2 || d.emailNo === 3 || d.emailNo === 4 || d.emailNo === 5
        ? status.copyShas[d.emailNo] ?? null
        : null,
    postal: status.gates.postal_address.value,
  });
  const cancelled = cancel.isSuccess ? readCancelResult(cancel.data) : null;
  return (
    <Card
      title="Dispatches"
      sub="Every seed and live batch the server reports. Mark a seed checked once you have read it in your own inbox; cancel a frozen or released batch to stop what has not gone yet."
    >
      {dispatches.length === 0 ? (
        <p className="text-sm text-neutral">None yet.</p>
      ) : (
        <div className="overflow-x-auto min-w-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-neutral">
              <tr className="border-b border-gray-100">
                <th className="pb-2 pr-3 font-medium">Batch</th>
                <th className="pb-2 pr-3 font-medium">Email</th>
                <th className="pb-2 pr-3 font-medium">Kind</th>
                <th className="pb-2 pr-3 font-medium">Version</th>
                <th className="pb-2 pr-3 font-medium">State</th>
                <th className="pb-2 pr-3 font-medium">Frozen</th>
                <th className="pb-2 pr-3 font-medium">Sends</th>
                <th className="pb-2 pr-3 font-medium">Copy sha</th>
                <th className="pb-2 pr-3 font-medium">Released</th>
                <th className="pb-2 pr-3 font-medium">Frozen at</th>
                <th className="pb-2 font-medium">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {dispatches.map((d) => (
                <tr key={d.id} className="border-b border-gray-50 align-top">
                  <td className="py-2 pr-3 font-mono text-[11px] break-all min-w-[9rem]">{d.id}</td>
                  <td className="py-2 pr-3">{d.emailNo ?? '-'}</td>
                  <td className="py-2 pr-3">{d.kind || '-'}</td>
                  <td className="py-2 pr-3">{d.variant ?? '-'}</td>
                  <td className="py-2 pr-3">{d.state || '-'}</td>
                  <td className="py-2 pr-3 tabular-nums">{d.frozenCount ?? '-'}</td>
                  <td className="py-2 pr-3 text-xs">
                    {d.sends.filter((r) => r.value > 0).map((r) => `${r.label.toLowerCase()} ${r.value}`).join(', ') || '-'}
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs">{d.copySha ? shaPrefix(d.copySha) : '-'}</td>
                  <td className="py-2 pr-3 text-xs whitespace-nowrap">
                    {d.releasedAt ? fmtTime(d.releasedAt) : d.releasedBy ?? '-'}
                  </td>
                  <td className="py-2 pr-3 text-xs text-neutral whitespace-nowrap">{fmtTime(d.createdAt) || '-'}</td>
                  <td className="py-2 text-xs whitespace-nowrap">
                    {seedCheckedBody(d, checkedList(d), dispatches) &&
                      (seedMarkProblem(d, seedCtx(d)) !== null ? (
                        <span className="block min-w-[12rem] whitespace-normal text-amber-800">
                          {seedMarkProblem(d, seedCtx(d))}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => markSeedChecked(d, checkedList(d), dispatches, markWrite, ask, marks.lock)}
                          disabled={marks.held}
                          className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Mark checked
                        </button>
                      ))}
                    {isCancellable(d) && (
                      <button
                        type="button"
                        onClick={() => cancelBatch(d, (body) => cancel.mutate(body), ask)}
                        disabled={cancel.isPending}
                        aria-label={`Cancel this batch: email ${d.emailNo}, ${unsentCount(d)} not sent yet`}
                        className="px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Cancel this batch
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {mark.isError && <ErrorBox>Mark checked: {describeError(mark.error)}</ErrorBox>}
      {mark.isSuccess && (
        <Ok>
          Marked checked: {mark.variables?.key} now lists{' '}
          <span className="font-mono break-all">{mark.variables?.value}</span>.
        </Ok>
      )}
      {cancel.isError && <ErrorBox>Cancel: {describeError(cancel.error)}</ErrorBox>}
      {cancel.isSuccess && (
        <Ok>
          {cancelled
            ? `Cancelled. ${cancelled.deleted} not yet sent ${cancelled.deleted === 1 ? 'was' : 'were'} removed; ` +
              `${cancelled.kept} already sent or in flight ${cancelled.kept === 1 ? 'stays' : 'stay'} on record, so nobody is sent twice.`
            : 'Cancelled.'}
        </Ok>
      )}
    </Card>
  );
}

function RecipientCard({ slug, retryable }: { slug: string; retryable: string[] }) {
  const replied = useDiscoveryAction<{ recipientId: string } | { email: string }>(slug, 'mark-replied');
  const retry = useDiscoveryAction<{ recipientId: string }>(slug, 'retry-notices');
  const [who, setWho] = useState('');
  const value = who.trim();
  const isEmail = EMAIL_RX.test(value);
  const isId = !isEmail && UUID_RX.test(value);
  return (
    <Card
      title="One brother"
      sub="Mark replied when a brother answers by email instead of on the page; it takes his recipient id or his email. Retry notices sends an answered brother's confirmation and notices again; it takes his recipient id."
    >
      <label htmlFor="dz-recipient" className="block text-sm font-medium text-navy">
        Recipient id or email
      </label>
      <input
        id="dz-recipient"
        value={who}
        onChange={(e) => setWho(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        className="mt-1 w-full sm:w-96 max-w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
      />
      <div className="flex gap-2 mt-2 flex-wrap">
        <button
          type="button"
          onClick={() => replied.mutate(isEmail ? { email: value } : { recipientId: value })}
          disabled={!(isEmail || isId) || replied.isPending}
          className="px-3 py-1.5 rounded bg-navy text-white text-sm font-medium disabled:opacity-50"
        >
          {replied.isPending ? 'Marking...' : 'Mark replied'}
        </button>
        <button
          type="button"
          onClick={() => retry.mutate({ recipientId: value })}
          disabled={!isId || retry.isPending}
          className="px-3 py-1.5 rounded border border-gray-300 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
        >
          {retry.isPending ? 'Sending...' : 'Retry notices'}
        </button>
      </div>
      {replied.isError && (
        <ErrorBox>
          Mark replied:{' '}
          {describeRecipientError(replied.error, replied.variables && 'email' in replied.variables ? 'email' : 'id')}
        </ErrorBox>
      )}
      {replied.isSuccess && <Ok>Marked replied.</Ok>}
      {retry.isError && <ErrorBox>Retry notices: {describeError(retry.error)}</ErrorBox>}
      {retry.isSuccess &&
        (retryNoticesOutcome(retry.data).ok ? (
          <Ok>{retryNoticesOutcome(retry.data).text}</Ok>
        ) : (
          <ErrorBox>Retry notices: {retryNoticesOutcome(retry.data).text}</ErrorBox>
        ))}
      {retryable.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100 min-w-0">
          <p className="text-xs font-medium text-navy">Waiting for a retry: {retryable.length}</p>
          <p className="text-[11px] text-neutral">
            Answered brothers whose confirmation or notices did not go, by opaque id.
          </p>
          <ul className="mt-2 space-y-1 min-w-0">
            {retryable.map((rid) => (
              <li key={rid} className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-xs break-all">{rid}</span>
                <button
                  type="button"
                  onClick={() => retry.mutate({ recipientId: rid })}
                  disabled={retry.isPending}
                  aria-label={`Retry notices for ${rid}`}
                  className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 flex-shrink-0"
                >
                  Retry
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Small parts

function Card({ title, sub, children }: { title: string; sub?: string; children: ReactNode }) {
  const id = `dz-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <section aria-labelledby={id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 min-w-0">
      <h3 id={id} className="text-navy text-sm font-semibold">
        {title}
      </h3>
      {sub && <p className="text-xs text-neutral mt-1">{sub}</p>}
      <div className="mt-3 min-w-0">{children}</div>
    </section>
  );
}

function CountGrid({ rows, empty }: { rows: CountRow[]; empty: string }) {
  if (rows.length === 0) return empty ? <p className="text-sm text-neutral">{empty}</p> : null;
  return (
    <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 min-w-0">
      {rows.map((r) => (
        <div key={r.label} className="rounded-lg border border-gray-100 bg-gray-50 p-3 min-w-0">
          <dt className="text-[11px] uppercase tracking-wide text-neutral truncate" title={r.label}>
            {r.label}
          </dt>
          <dd className="text-navy text-xl font-bold tabular-nums">{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function EmailSelect({ id, value, onChange }: { id: string; value: EmailNo; onChange: (v: EmailNo) => void }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-xs font-medium text-navy">
        Email
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(Number(e.target.value) as EmailNo)}
        className="mt-1 border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
      >
        {EMAIL_NOS.map((n) => (
          <option key={n} value={n}>
            Email {n}
          </option>
        ))}
      </select>
    </div>
  );
}

function VariantSelect({ id, value, onChange }: { id: string; value: Variant; onChange: (v: Variant) => void }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-xs font-medium text-navy">
        Version
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as Variant)}
        className="mt-1 border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
      >
        {VARIANTS.map((v) => (
          <option key={v.value} value={v.value}>
            {v.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function ErrorBox({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="mt-3 border border-red-200 bg-red-50 text-red-800 text-sm rounded p-2 break-words">
      {children}
    </div>
  );
}

function Ok({ children }: { children: ReactNode }) {
  return (
    <div role="status" className="mt-3 border border-green-200 bg-green-50 text-green-800 text-sm rounded p-2 break-words">
      {children}
    </div>
  );
}

function fmtTime(iso: string | null): string {
  if (!iso) return '';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return iso;
  return t.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
